<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Button, Dropdown, ScrollArea, Avatar, Sidebar } from 'frappe-ui'
import {
  PopoverContent,
  PopoverPortal,
  PopoverRoot,
  PopoverTrigger,
} from 'reka-ui'
import { session, logout, switchCompany, isImpersonating, endImpersonation } from '../session'
import { can, canAccessRoute } from '../rbac'
import { isNavItemActive, navGroups } from '../navigation'
import { useTheme } from '../composables/useTheme'
import BrandLogo from '../components/BrandLogo.vue'
import ProductOverlays from '../components/ProductOverlays.vue'
import NotificationBell from '../components/NotificationBell.vue'
import CommandPalette from '../components/CommandPalette.vue'
import SidebarNav from '../components/SidebarNav.vue'
import { useNavBadges } from '../composables/useNavBadges'
import { useRecentPages } from '../composables/useRecentPages'

const route = useRoute()
const router = useRouter()
const groups = computed(() => navGroups())

/* localStorage can throw in private mode / when quota is full. Never let that break the shell. */
function readStored(key, fallback) {
  try {
    return localStorage.getItem(key) ?? fallback
  } catch {
    return fallback
  }
}

function store(key, value) {
  try {
    localStorage.setItem(key, value)
  } catch {
    // Preferences just won't persist.
  }
}

const { badges } = useNavBadges()
const { record } = useRecentPages()
const paletteOpen = ref(false)
const mainEl = ref(null)

function badgeFor(item) {
  return badges.value[item.route] || 0
}

const mobileOpen = ref(false)
const sidebarCollapsed = ref(readStored('church_sidebar_collapsed', 'false') === 'true')
const profileOpen = ref(false)
const endingImpersonation = ref(false)
const { preference, resolved, setTheme } = useTheme()

const displayName = computed(() => session.boot.full_name || session.boot.user || 'Account')
const displayEmail = computed(() => session.boot.user_email || session.boot.user || '')
// The product role, not Frappe's technical ones (Desk User / All / Guest).
const ROLE_ORDER = [
  'System Manager',
  'Church Access Manager',
  'Parish Admin',
  'Finance Officer',
  'Clergy',
  'Safe Environment Officer',
  'Catechist',
  'Church Member',
]
const displayRole = computed(() => {
  const held = new Set(session.boot.roles || [])
  return ROLE_ORDER.find((role) => held.has(role)) || 'Member'
})

const createItems = computed(() =>
  [
    { label: 'Record gift', icon: 'lucide-hand-coins', show: can('giving.manage'), route: '/giving/new' },
    { label: 'Record pledge', icon: 'lucide-hand-heart', show: can('pledges.manage'), route: '/pledges/new' },
    { label: 'Mass intention', icon: 'lucide-church', show: can('mass_intentions.manage'), route: '/mass-intentions/new' },
    { label: 'Sacramental record', icon: 'lucide-scroll-text', show: can('sacraments.manage'), route: '/sacraments/record/new' },
    { label: 'Support ticket', icon: 'lucide-life-buoy', show: can('support.view'), route: '/support/new' },
  ].filter((item) => item.show),
)

const quickActionMenu = computed(() => {
  const groups = []
  if (createItems.value.length) {
    groups.push({
      group: 'Create',
      options: createItems.value.map((item) => ({
        label: item.label,
        icon: item.icon,
        onClick: () => goTo(item.route),
      })),
    })
  }
  groups.push({
    group: 'Go to',
    options: [
      { label: 'Notifications', icon: 'lucide-bell', onClick: () => goTo('/notifications') },
      { label: 'Members', icon: 'lucide-users-round', show: can('members.view'), onClick: () => goTo('/members') },
    ].filter((item) => item.show !== false),
  })
  return groups.filter((group) => group.options.length)
})

const helpMenu = computed(() =>
  [
    { label: 'Support', icon: 'lucide-life-buoy', show: can('support.view'), onClick: () => goTo('/support') },
    { label: "What's new", icon: 'lucide-sparkles', show: true, onClick: () => goTo('/whats-new') },
  ]
    .filter((item) => item.show)
    .map(({ show, ...item }) => item),
)

const shortcutLabel = computed(() =>
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '') ? '⌘K' : 'Ctrl K',
)

// Every destination in the single sidebar, so the palette can jump anywhere.
const paletteEntries = computed(() => {
  const out = []
  for (const item of createItems.value) {
    out.push({ key: `create:${item.route}`, label: item.label, icon: item.icon, route: item.route, section: 'Create' })
  }
  for (const group of groups.value) {
    for (const item of group.items) {
      out.push({
        key: `${group.key}:${item.route}`,
        label: item.label,
        icon: item.icon,
        route: item.route,
        section: group.label,
        badge: badgeFor(item),
      })
    }
  }
  return out
})

// Orientation at a glance: current page, from route meta or the longest matching nav route.
const currentTitle = computed(() => {
  if (route.meta?.title) return route.meta.title
  let best = null
  for (const group of groups.value) {
    for (const item of group.items) {
      if (!isNavItemActive(item, route.path)) continue
      if (!best || item.route.length > best.route.length) best = item
    }
  }
  return best ? best.label : ''
})

const initials = computed(() => {
  const name = displayName.value || 'U'
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
})

watch(
  () => route.fullPath,
  () => {
    mobileOpen.value = false
    profileOpen.value = false
    record(route.path)
  },
)

watch(sidebarCollapsed, (value) => {
  store('church_sidebar_collapsed', String(value))
})

// The scroll container is shared by every page; without this a long page leaves the next one
// scrolled halfway down.
watch(
  () => route.path,
  () => mainEl.value?.scrollTo({ top: 0 }),
)

async function stopImpersonating() {
  endingImpersonation.value = true
  try {
    await endImpersonation()
    router.go(0)
  } finally {
    endingImpersonation.value = false
  }
}

// Reactive: the toggle's aria-label/aria-expanded must follow a resize or rotation.
const desktop = ref(typeof window !== 'undefined' ? window.matchMedia('(min-width: 768px)').matches : true)
const wide = ref(typeof window !== 'undefined' ? window.matchMedia('(min-width: 1024px)').matches : true)
// On medium desktop widths the sidebar starts as an icon rail; the user can still expand it.
const mediumExpanded = ref(false)
let desktopQuery = null
let wideQuery = null
function syncDesktop(event) {
  desktop.value = event.matches
}
function syncWide(event) {
  wide.value = event.matches
  // A resize into wide screens should not keep a temporary medium-width override.
  if (event.matches) mediumExpanded.value = false
}
function onKeydown(event) {
  if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
    event.preventDefault()
    paletteOpen.value = !paletteOpen.value
  } else if (event.key === 'Escape' && mobileOpen.value) {
    mobileOpen.value = false
  }
}

onMounted(() => {
  desktopQuery = window.matchMedia('(min-width: 768px)')
  desktop.value = desktopQuery.matches
  desktopQuery.addEventListener('change', syncDesktop)
  wideQuery = window.matchMedia('(min-width: 1024px)')
  wide.value = wideQuery.matches
  wideQuery.addEventListener('change', syncWide)
  window.addEventListener('keydown', onKeydown)
})
onUnmounted(() => {
  desktopQuery?.removeEventListener('change', syncDesktop)
  wideQuery?.removeEventListener('change', syncWide)
  window.removeEventListener('keydown', onKeydown)
})

function isDesktop() {
  return desktop.value
}

// Collapsing is a desktop-only concept. On mobile the drawer is always full width, even if the
// user collapsed the desktop sidebar earlier (otherwise the drawer opens as a 3rem sliver).
const effectiveCollapsed = computed({
  get: () =>
    desktop.value &&
    (sidebarCollapsed.value || (!wide.value && !mediumExpanded.value)),
  set: (value) => {
    if (!desktop.value) return
    if (wide.value) sidebarCollapsed.value = value
    else mediumExpanded.value = !value
  },
})

const sidebarBg = computed(() => (resolved.value === 'dark' ? 'bg-surface-gray-1' : 'bg-surface-white'))

/** One control for both modes: collapse on desktop, drawer on mobile. */
function toggleSidebar() {
  if (isDesktop()) {
    effectiveCollapsed.value = !effectiveCollapsed.value
  } else {
    mobileOpen.value = !mobileOpen.value
  }
}

const sidebarExpanded = computed(() => (desktop.value ? !effectiveCollapsed.value : mobileOpen.value))
const toggleLabel = computed(() =>
  desktop.value
    ? effectiveCollapsed.value
      ? 'Expand sidebar'
      : 'Collapse sidebar'
    : mobileOpen.value
      ? 'Close menu'
      : 'Open menu',
)

function go(path) {
  let target = path || '/'
  if (target.startsWith('/church')) target = target.slice('/church'.length) || '/'
  router.push(target)
  mobileOpen.value = false
}

function openSettings() {
  goTo(canProfile.value ? '/my/profile' : '/my')
}

function openSecurity() {
  goTo('/my/profile?tab=security')
}

function openHelp() {
  goTo(can('support.view') ? '/support' : '/whats-new')
}

function openDesk() {
  profileOpen.value = false
  window.open(session.boot.desk_url || '/app', '_blank')
}

async function onLogout() {
  profileOpen.value = false
  await logout()
  router.push({ name: 'Login' })
}

function goTo(path) {
  profileOpen.value = false
  router.push(path)
}

function onPaletteSelect(entry) {
  go(entry.route)
}

async function onCompanyChange(value) {
  if (!value || value === session.boot.company) return
  await switchCompany(value)
  router.go(0)
}

const companyMenu = computed(() =>
  [
    {
      group: 'Parish / branch',
      options: (session.boot.companies || []).map((c) => ({
        label: c,
        icon: c === session.boot.company ? 'lucide-check' : 'lucide-building-2',
        onClick: () => onCompanyChange(c),
      })),
    },
  ],
)
const hasCompanies = computed(() => (session.boot.companies || []).length > 0)

// Gate the account menu with the same route guards the pages use.
const canProfile = computed(() => canAccessRoute(router.resolve('/my/profile').meta))
const canHelp = computed(() => canAccessRoute(router.resolve('/whats-new').meta))

const themes = [
  { value: 'light', label: 'Light', icon: 'lucide-sun' },
  { value: 'dark', label: 'Dark', icon: 'lucide-moon' },
  { value: 'system', label: 'System', icon: 'lucide-monitor' },
]
</script>

<template>
  <div class="flex h-screen w-full overflow-hidden bg-surface-base text-ink-gray-9">
    <a
      href="#main-content"
      class="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-[140] focus:rounded focus:bg-surface-white focus:px-3 focus:py-2 focus:text-sm focus:text-ink-gray-9 focus:shadow-lg"
    >
      Skip to content
    </a>

    <!-- Mobile scrim -->
    <div
      v-if="mobileOpen"
      class="fixed inset-0 z-30 bg-black/50 md:hidden"
      aria-hidden="true"
      @click="mobileOpen = false"
    />

    <!--
      Opaque solid sidebar so nav links stay readable on mobile (no transparent overlay).
      One continuous column: pinned parish switcher + search, one scrolling nav, pinned account.
    -->
    <!-- z-40 mobile drawer; dialogs use z-100 so SettingsDialog is never under the sidebar -->
    <aside
      id="app-sidebar"
      class="fixed inset-y-0 left-0 z-40 flex shrink-0 flex-col border-r border-outline-gray-1 shadow-xl transition-transform duration-200 md:static md:z-auto md:translate-x-0 md:shadow-none"
      :class="[mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0', sidebarBg]"
    >
      <Sidebar v-model:collapsed="effectiveCollapsed" width="15rem" collapsed-width="3rem" class="!h-full !border-0">
        <!-- 1. Parish / branch switcher, pinned at the top (logo, name, chevron). -->
        <div class="flex shrink-0 items-center gap-1 px-2 pb-1 pt-2">
          <Dropdown v-if="hasCompanies" :options="companyMenu" class="min-w-0 flex-1">
            <button
              type="button"
              class="flex h-10 w-full min-w-0 items-center gap-2 rounded-md px-1.5 text-left transition hover:bg-surface-gray-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-outline-gray-4"
              :class="effectiveCollapsed ? 'justify-center !px-0' : ''"
              :aria-label="`Switch parish or branch. Current: ${session.boot.company || 'none'}`"
            >
              <BrandLogo :size="24" />
              <span v-if="!effectiveCollapsed" class="flex min-w-0 flex-1 flex-col leading-tight">
                <span class="truncate text-sm font-medium text-ink-gray-9">
                  {{ session.boot.company || 'Select parish' }}
                </span>
                <span class="truncate text-[11px] text-ink-gray-5">Catolica</span>
              </span>
              <span
                v-if="!effectiveCollapsed"
                class="lucide-chevrons-up-down size-4 shrink-0 text-ink-gray-5"
                aria-hidden="true"
              />
            </button>
          </Dropdown>
          <div v-else class="flex min-w-0 flex-1 items-center gap-2 px-1" :class="effectiveCollapsed ? 'justify-center !px-0' : ''">
            <BrandLogo :size="24" />
            <div v-if="!effectiveCollapsed" class="min-w-0 flex-1 leading-tight">
              <div class="truncate text-sm font-medium text-ink-gray-9">{{ session.boot.company || 'Catolica' }}</div>
              <div class="truncate text-[11px] text-ink-gray-5">Catolica</div>
            </div>
          </div>
          <Button class="md:hidden" variant="ghost" icon="lucide-x" aria-label="Close menu" @click="mobileOpen = false" />
        </div>

        <!-- 2. Quick search / command palette. -->
        <div class="shrink-0 px-2 pb-2 pt-1">
          <button
            v-if="!effectiveCollapsed"
            type="button"
            class="flex h-8 w-full items-center gap-2 rounded-md border border-outline-gray-2 px-2 text-sm text-ink-gray-6 transition hover:bg-surface-gray-2 hover:text-ink-gray-9 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-outline-blue-4"
            aria-label="Search pages and actions"
            aria-keyshortcuts="Control+K Meta+K"
            data-testid="open-palette"
            @click="paletteOpen = true"
          >
            <span class="lucide-search size-4 shrink-0" aria-hidden="true" />
            <span class="flex-1 text-left">Search</span>
            <kbd class="rounded bg-surface-gray-2 px-1.5 text-[11px] text-ink-gray-6">{{ shortcutLabel }}</kbd>
          </button>
          <button
            v-else
            type="button"
            class="flex h-9 w-full items-center justify-center rounded-md text-ink-gray-6 transition hover:bg-surface-gray-2 hover:text-ink-gray-9 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-outline-blue-4"
            :title="`Search (${shortcutLabel})`"
            aria-label="Search pages and actions"
            aria-keyshortcuts="Control+K Meta+K"
            data-testid="open-palette"
            @click="paletteOpen = true"
          >
            <span class="lucide-search size-4" aria-hidden="true" />
          </button>
        </div>

        <!-- 3. The only scrolling area: labelled collapsible groups (or the icon rail). -->
        <ScrollArea class="min-h-0 flex-1" :class="sidebarBg" viewport-class="px-2 pb-3">
          <SidebarNav
            :groups="groups"
            :collapsed="effectiveCollapsed"
            :badges="badges"
            @navigate="go"
          />
        </ScrollArea>

        <!-- 4. The only bottom element: compact account menu. -->
        <div class="shrink-0 p-2" :class="sidebarBg">
          <PopoverRoot v-model:open="profileOpen">
            <PopoverTrigger as-child>
              <button
                type="button"
                class="flex h-12 w-full items-center gap-2 rounded-md px-1.5 text-left transition hover:bg-surface-gray-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-outline-gray-4"
                :class="effectiveCollapsed ? 'justify-center !px-0' : ''"
                :aria-label="`Account menu, ${displayName}, ${displayRole}`"
                aria-haspopup="dialog"
              >
                <Avatar :label="initials" :image="session.boot.user_image" size="sm" />
                <span v-if="!effectiveCollapsed" class="flex min-w-0 flex-1 flex-col leading-tight">
                  <span class="truncate text-sm font-medium text-ink-gray-9">{{ displayName }}</span>
                  <span class="truncate text-[11px] text-ink-gray-5">{{ displayRole }}</span>
                </span>
                <span
                  v-if="!effectiveCollapsed"
                  class="lucide-chevrons-up-down size-3.5 shrink-0 text-ink-gray-5"
                  aria-hidden="true"
                />
              </button>
            </PopoverTrigger>
            <PopoverPortal>
              <PopoverContent
                side="top"
                align="start"
                :side-offset="8"
                class="z-[120] w-64 overflow-hidden rounded-lg border border-outline-gray-1 bg-surface-elevation-2 shadow-xl outline-none"
                data-testid="account-menu"
              >
                <div class="flex items-center gap-2 border-b border-outline-gray-1 px-3 py-3">
                  <Avatar :label="initials" :image="session.boot.user_image" size="lg" />
                  <div class="min-w-0">
                    <div class="truncate text-sm font-semibold text-ink-gray-9">{{ displayName }}</div>
                    <div class="truncate text-xs text-ink-gray-5">{{ displayEmail }}</div>
                    <div class="truncate text-xs text-ink-gray-5">
                      {{ displayRole }}<template v-if="session.boot.company"> · {{ session.boot.company }}</template>
                    </div>
                  </div>
                </div>

                <div class="py-1">
                  <button v-if="canProfile" type="button" class="app-menu-item" @click="goTo('/my/profile')">
                    <span class="lucide-user size-4 text-ink-gray-5" aria-hidden="true" />
                    Profile
                  </button>
                  <button v-if="canProfile" type="button" class="app-menu-item" @click="openSecurity()">
                    <span class="lucide-shield-check size-4 text-ink-gray-5" aria-hidden="true" />
                    Security &amp; 2FA
                  </button>
                  <button v-if="canHelp" type="button" class="app-menu-item" @click="openHelp()">
                    <span class="lucide-circle-help size-4 text-ink-gray-5" aria-hidden="true" />
                    Help
                  </button>
                  <button
                    v-if="session.boot.can_access_desk"
                    type="button"
                    class="app-menu-item"
                    @click="openDesk"
                  >
                    <span class="lucide-external-link size-4 text-ink-gray-5" aria-hidden="true" />
                    Open Desk
                  </button>
                </div>

                <div class="border-t border-outline-gray-1 py-1">
                  <div class="px-3 pb-1 pt-1 text-[11px] font-semibold uppercase tracking-wide text-ink-gray-5">
                    Theme
                  </div>
                  <button
                    v-for="theme in themes"
                    :key="theme.value"
                    type="button"
                    class="app-menu-item"
                    @click="setTheme(theme.value)"
                  >
                    <span :class="[theme.icon, 'size-4 text-ink-gray-5']" aria-hidden="true" />
                    <span class="flex-1 text-left">{{ theme.label }}</span>
                    <span
                      v-if="preference === theme.value"
                      class="lucide-check size-4 text-ink-gray-7"
                      aria-label="Selected"
                    />
                  </button>
                </div>

                <div class="border-t border-outline-gray-1 p-1">
                  <button
                    type="button"
                    class="app-menu-item text-ink-red-7 hover:bg-surface-red-2 dark:text-ink-red-4"
                    @click="onLogout"
                  >
                    <span class="lucide-log-out size-4" aria-hidden="true" />
                    Sign out
                  </button>
                </div>
              </PopoverContent>
            </PopoverPortal>
          </PopoverRoot>
        </div>
      </Sidebar>
    </aside>

    <div class="flex min-w-0 flex-1 flex-col">
      <div
        v-if="isImpersonating()"
        class="flex shrink-0 items-center justify-between bg-amber-50 px-4 py-2 text-sm text-amber-800 dark:bg-amber-900/20 dark:text-amber-200"
      >
        <div class="flex items-center gap-2">
          <span class="lucide-eye size-4" aria-hidden="true" />
          <span>
            Impersonating as
            <span class="font-medium">{{ displayName }}</span>
          </span>
        </div>
        <Button
          size="sm"
          variant="subtle"
          label="Stop impersonating"
          :loading="endingImpersonation"
          @click="stopImpersonating"
        />
      </div>

      <header class="flex h-12 shrink-0 items-center gap-2 border-b border-outline-gray-1 bg-surface-white px-3 md:px-4">
        <!-- Left: the single sidebar toggle (desktop collapse + mobile drawer), then where you are -->
        <button
          type="button"
          class="inline-flex size-8 shrink-0 items-center justify-center rounded text-ink-gray-7 transition hover:bg-surface-gray-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-outline-gray-4"
          :aria-label="toggleLabel"
          :aria-expanded="sidebarExpanded"
          aria-controls="app-sidebar"
          :title="toggleLabel"
          @click="toggleSidebar"
        >
          <span class="lucide-menu size-4" aria-hidden="true" />
        </button>

        <div class="min-w-0 flex-1 truncate text-sm font-medium text-ink-gray-8" data-testid="header-title">
          {{ currentTitle }}
        </div>

        <!-- Right: help and notifications; account lives at the bottom of the sidebar. -->
        <div class="flex shrink-0 items-center gap-1">
          <Dropdown :options="helpMenu">
            <button
              type="button"
              class="inline-flex size-8 items-center justify-center rounded text-ink-gray-6 transition hover:bg-surface-gray-2 hover:text-ink-gray-9 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-outline-gray-4"
              aria-label="Help and support"
              title="Help and support"
            >
              <span class="lucide-circle-help size-4" aria-hidden="true" />
            </button>
          </Dropdown>

          <NotificationBell @open-settings="openSettings" />
        </div>
      </header>

      <main
        id="main-content"
        ref="mainEl"
        tabindex="-1"
        class="app-main-scroll min-h-0 flex-1 overflow-y-auto p-4 pb-4 focus:outline-none md:p-6 md:pb-4"
      >
        <ProductOverlays class="mb-4" />
        <slot />
      </main>
    </div>

    <CommandPalette v-model:open="paletteOpen" :entries="paletteEntries" @select="onPaletteSelect" />
  </div>
</template>
